import React, { useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { zohoAPI } from '@/lib/api';
import { toast } from 'sonner';
import { Webhook, Copy, Check, CheckCircle2, Clock, AlertCircle, Ban, RefreshCw } from 'lucide-react';

const STATUS_MAP = {
  active: { variant: 'default', label: 'Active', Icon: CheckCircle2 },
  pending_mapping: { variant: 'secondary', label: 'Pending Mapping', Icon: Clock },
  pending_test: { variant: 'outline', label: 'Pending Test', Icon: AlertCircle },
  disabled: { variant: 'destructive', label: 'Disabled', Icon: Ban },
};

function StatusBadge({ status }) {
  const cfg = STATUS_MAP[status] || { variant: 'outline', label: status, Icon: AlertCircle };
  return (
    <Badge variant={cfg.variant} className="gap-1" data-testid={`status-badge-${status}`}>
      <cfg.Icon className="h-3 w-3" />{cfg.label}
    </Badge>
  );
}

function CopyUrlButton({ slug }) {
  const [copied, setCopied] = useState(false);
  if (!slug) return <span className="text-xs text-muted-foreground">Legacy</span>;
  const url = zohoAPI.getWebhookUrl(slug);
  const handleCopy = (e) => {
    e.stopPropagation();
    navigator.clipboard.writeText(url);
    setCopied(true);
    toast.success('Webhook URL copied');
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <Button variant="ghost" size="sm" onClick={handleCopy} data-testid={`copy-url-${slug}`} className="h-7 px-2 text-xs gap-1">
      {copied ? <Check className="h-3 w-3 text-green-500" /> : <Copy className="h-3 w-3" />}
      {copied ? 'Copied' : 'Copy URL'}
    </Button>
  );
}

export default function ZohoFormList({ forms, categories, onSelectForm, onRefresh }) {
  const [filterCat, setFilterCat] = useState('all');

  const filtered = filterCat === 'all'
    ? forms
    : filterCat === 'uncategorized'
      ? forms.filter(f => !f.category_id)
      : forms.filter(f => f.category_id === filterCat);

  return (
    <div className="space-y-4">
      {/* Filter bar */}
      <div className="flex items-center gap-3">
        <Select value={filterCat} onValueChange={setFilterCat}>
          <SelectTrigger className="w-[200px]" data-testid="category-filter">
            <SelectValue placeholder="All Categories" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Categories</SelectItem>
            <SelectItem value="uncategorized">Uncategorized</SelectItem>
            {categories.map(c => (
              <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button variant="outline" size="sm" onClick={onRefresh} data-testid="refresh-forms-btn">
          <RefreshCw className="h-4 w-4 mr-1" />Refresh
        </Button>
      </div>

      {/* Forms table */}
      <Card>
        <CardContent className="p-0">
          {filtered.length === 0 ? (
            <div className="text-center py-16 text-muted-foreground" data-testid="empty-forms">
              <Webhook className="h-12 w-12 mx-auto mb-3 opacity-20" />
              <p className="font-medium">No forms configured</p>
              <p className="text-sm mt-1">Click "Add Form" to create your first form configuration</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Form Name</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead>Webhook</TableHead>
                  <TableHead className="text-center">Submissions</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map(f => (
                  <TableRow
                    key={f.id}
                    className="cursor-pointer hover:bg-muted/50"
                    onClick={() => onSelectForm(f.id)}
                    data-testid={`form-row-${f.id}`}
                  >
                    <TableCell>
                      <div className="font-medium">{f.form_name}</div>
                      {f.description && <div className="text-xs text-muted-foreground mt-0.5 line-clamp-1">{f.description}</div>}
                    </TableCell>
                    <TableCell>
                      {f.category_name ? (
                        <Badge variant="outline" className="text-xs">{f.category_name}</Badge>
                      ) : (
                        <span className="text-xs text-muted-foreground">—</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <CopyUrlButton slug={f.slug} />
                    </TableCell>
                    <TableCell className="text-center font-mono text-sm">{f.submission_count || 0}</TableCell>
                    <TableCell><StatusBadge status={f.status} /></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
